import { useEffect, useMemo, useRef, type ReactElement } from 'react'
import { useFormContext, useFormState, useWatch, type FieldValues } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { filter, slice, uniq } from 'lodash-es'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getCalcChecksForElementRequest2 } from '@queries'
import type { DesignFormValues } from '../types'
import { hydrateCrossSectionForCalcElementChecksRequest } from '../utils'

/** Nur `crossSection` / member settings zählen — nicht z.B. Kommentare oder berechnete Nachweise. */
function sliceHasRecalcInputDirty(dirtySlice: unknown): boolean {
  if (!dirtySlice || typeof dirtySlice !== 'object') return false
  const d = dirtySlice as Record<string, unknown>
  return Boolean(d.crossSection ?? d.memberCheckSettingsSelectedElement)
}

function elementIndicesWithRecalcDirtyFields(
  dirtyElements: unknown,
  elementsLength: number,
): number[] {
  if (!dirtyElements || !elementsLength) return []
  const out: number[] = []
  const de = dirtyElements as Record<number, unknown>
  for (let i = 0; i < elementsLength; i++) {
    if (sliceHasRecalcInputDirty(de[i])) out.push(i)
  }
  return out
}

function elementIndicesWithChangedCalcInputs(
  prevSerialized: string,
  nextSerialized: string,
  elementsLength: number,
): number[] {
  if (prevSerialized === nextSerialized || !elementsLength) return []
  try {
    const prev = JSON.parse(prevSerialized) as unknown[]
    const next = JSON.parse(nextSerialized) as unknown[]
    const out: number[] = []
    for (let i = 0; i < elementsLength; i++) {
      if (JSON.stringify(prev[i]) !== JSON.stringify(next[i])) out.push(i)
    }
    return out
  } catch {
    return [0]
  }
}

function omitNonCalcMemberSettings(settings: SettingsOnMember | null): SettingsOnMember | null {
  if (!settings) return settings
  const { check_specific_settings: _checkSpecificSettings, ...calcSettings } = settings
  return calcSettings as SettingsOnMember
}

/**
 * Serialized snapshot of inputs that drive the calc-checks request.
 *
 * We diff fingerprints (prev vs current) to decide when to recalculate — not
 * `dirtyFields`. RHF clears dirty state when a value matches `defaultValues` again
 * (e.g. width 12 → 123 → 12), but `checksSelectedElement` is written with
 * `shouldDirty: false` and would still show eta from the intermediate value.
 */
function serializeElementCalcInputs(elements: DesignFormValues[] | undefined): string {
  if (!elements?.length) return '[]'
  return JSON.stringify(
    elements.map(e => ({
      g: e.elementGuid,
      cs: e.crossSection,
      m: omitNonCalcMemberSettings(e.memberCheckSettingsSelectedElement),
    })),
  )
}

const checkSettingsRecalculationMutationKey = (projectId: string | undefined, elementId?: string) =>
  ['checkSettingsRecalculation', projectId, elementId] as const

/** Für `useIsMutating` (Header/Pending-Anzeige beim Nachrechnen). */
export const checkSettingsRecalculationMutationFilter = (
  projectId: string | undefined,
  elementId?: string,
) => ({
  mutationKey: checkSettingsRecalculationMutationKey(projectId, elementId),
})

export const CheckSettingsRecalculation = ({
  elementId,
  enabled,
}: {
  elementId?: string
  enabled: boolean
}): ReactElement | null => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const abortRef = useRef<AbortController | null>(null)
  const prevCalculationInputFingerprintRef = useRef<string | null>(null)

  const { control, setValue, trigger, getValues } = useFormContext<FieldValues>()
  const { dirtyFields } = useFormState({ control })

  const watchedElements = useWatch({ name: 'elements' }) as DesignFormValues[] | undefined

  const calculationInputFingerprint = useMemo(
    () => serializeElementCalcInputs(watchedElements),
    [watchedElements],
  )

  const { mutate } = useMutation({
    mutationKey: checkSettingsRecalculationMutationKey(projectId, elementId),
    mutationFn: async ({ indices }: { indices: number[] }) => {
      const pid = projectId as string
      abortRef.current?.abort()
      const abortController = new AbortController()
      abortRef.current = abortController

      const root = getValues() as FieldValues & { elements?: DesignFormValues[] }
      const elements = root.elements ?? []

      const tasks = indices
        .map(i => {
          const elementSlice = elements[i]
          if (
            !elementSlice?.elementGuid ||
            !elementSlice.memberCheckSettingsSelectedElement ||
            !elementSlice.crossSection
          ) {
            return null
          }

          const supportingElementCSConfigBase =
            i === 0
              ? slice(elements, 1, elements.length + 1).map(el => ({
                  element_guid: el.elementGuid,
                  cross_section: el.crossSection,
                }))
              : []

          // for supporting elements that do not have a cross section (e.g. walls)
          // we need to filter them out
          const supportingElementCSConfig = filter(
            supportingElementCSConfigBase,
            config => !!config.cross_section,
          )

          return getCalcChecksForElementRequest2
            .request(pid, {
              requestData: {
                element_guid: elementSlice.elementGuid,
                crossSection: hydrateCrossSectionForCalcElementChecksRequest(
                  elementSlice.crossSection,
                ),
                settings: elementSlice.memberCheckSettingsSelectedElement,
                supporting_elements_cs_config: supportingElementCSConfig,
              },
              options: { signal: abortController.signal },
            })
            .then(data => ({ index: i, data }))
        })
        .filter((t): t is Promise<{ index: number; data: CrossSectionSuggestion }> => t != null)

      if (!tasks.length) return []

      return Promise.all(tasks)
    },
    onSuccess: results => {
      for (const { index, data } of results) {
        setValue(`elements.${index}.checksSelectedElement`, data.checks, {
          shouldDirty: false,
          shouldTouch: true,
        })
        setValue(`elements.${index}.supportConfigsForChecks`, data.support_configs_for_checks, {
          shouldDirty: false,
          shouldTouch: true,
        })
      }
    },
    onError: (error: Error) => {
      if ('message' in error && error.message === 'canceled') {
        return
      }
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Berechnen der Nachweise'),
        {
          variant: 'error',
        },
      )
    },
  })

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!enabled || !projectId) return

    const elements = (getValues('elements') as DesignFormValues[] | undefined) ?? []
    const prevFingerprint = prevCalculationInputFingerprintRef.current
    prevCalculationInputFingerprintRef.current = calculationInputFingerprint

    const hasRecalcInputs = (i: number) =>
      Boolean(
        elements[i]?.crossSection &&
          elements[i]?.memberCheckSettingsSelectedElement &&
          elements[i]?.elementGuid,
      )

    // empty support configs for checks means the element has just been selected
    // and we did not yet recalculate the checks for it
    let indices =
      elements[0]?.supportConfigsForChecks?.length === 0
        ? [0]
        : prevFingerprint !== null && prevFingerprint !== calculationInputFingerprint
        ? elementIndicesWithChangedCalcInputs(
            prevFingerprint,
            calculationInputFingerprint,
            elements.length,
          )
        : prevFingerprint === null
        ? elementIndicesWithRecalcDirtyFields(dirtyFields?.elements, elements.length)
        : []

    indices = uniq([0, ...indices.filter(hasRecalcInputs)])

    if (!indices.length) return

    Promise.all(indices.map(i => trigger(`elements.${i}` as const))).then(results => {
      if (!results.every(Boolean)) return
      mutate({ indices })
    })
  }, [
    calculationInputFingerprint,
    dirtyFields?.elements,
    enabled,
    getValues,
    mutate,
    projectId,
    trigger,
  ])

  return null
}
